class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    // Dopo una modifica di quantita' o una rimozione (cart.js ha gia'
    // sostituito .drawer__inner) ricarichiamo i suggerimenti correlati.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.annullaIscrizione = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'cart-items') this.caricaCorrelati();
      });
    }
  }

  disconnectedCallback() {
    if (this.annullaIscrizione) this.annullaIscrizione();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      // La barra della spedizione parte 120 ms dopo l'apertura (ritardo nel CSS).
      this.querySelector('barra-spedizione')?.avvia?.();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();

    this.caricaCorrelati();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', String(cartDrawerNote.closest('details').hasAttribute('open')));

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    // renderContents arriva sempre dopo un'aggiunta: il cassetto non e' piu' vuoto anche
    // quando a chiamarlo non e' product-form (es. "Completa il regalo", suggerimenti)
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // La riga appena aggiunta entra con opacita' + 8px (CSS .cart-item--nuova).
    if (parsedState.key && window.CSS?.escape) {
      this.querySelector(`.cart-item[data-chiave="${CSS.escape(parsedState.key)}"]`)?.classList.add('cart-item--nuova');
    }

    const giaAperto = this.classList.contains('active');
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });

    // Se il cassetto e' chiuso ci pensa open(); se e' gia' aperto (aggiunta da
    // "Aggiungi qualcosa di piccolo") il bottone premuto non esiste piu':
    // riportiamo il focus dentro il cassetto e ricarichiamo i suggerimenti.
    if (giaAperto) {
      const contenitore = document.getElementById('CartDrawer');
      if (contenitore && typeof trapFocus === 'function') {
        trapFocus(contenitore, this.querySelector('.cart-item--nuova .cart-item__name') || this.querySelector('.drawer__close'));
      }
      this.caricaCorrelati();
    }
  }

  /*
    "Aggiungi qualcosa di piccolo": il render del server parte dalla collezione
    scelta (o da tutti i prodotti). Qui chiediamo la stessa sezione all'endpoint
    dei consigli, che espone `recommendations` con i correlati del primo
    articolo, e sostituiamo il blocco se arriva qualcosa.
  */
  caricaCorrelati() {
    const blocco = this.querySelector('.cart-drawer__suggerimenti');
    if (!blocco) return;
    const primoProdotto = blocco.dataset.primoProdotto;
    if (!primoProdotto) return;
    if (blocco.dataset.fonte === 'correlati' && blocco.dataset.perProdotto === primoProdotto) return;

    this.richiestaCorrelati?.abort();
    const controllo = new AbortController();
    this.richiestaCorrelati = controllo;

    const base = blocco.dataset.url || '/recommendations/products';
    const url = `${base}?section_id=cart-drawer&product_id=${primoProdotto}&limit=10&intent=related`;

    fetch(url, { signal: controllo.signal })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const nuovo = html.querySelector('.cart-drawer__suggerimenti');
        const attuale = this.querySelector('.cart-drawer__suggerimenti');
        if (!nuovo || !attuale || nuovo.dataset.fonte !== 'correlati') return;
        if (!nuovo.querySelector('.cart-suggerimenti__voce')) return;
        nuovo.dataset.perProdotto = primoProdotto;
        // Se il focus era su un bottone della lista vecchia, non perderlo.
        const focusDentro = attuale.contains(document.activeElement);
        if (nuovo.innerHTML === attuale.innerHTML) {
          attuale.dataset.fonte = nuovo.dataset.fonte;
          attuale.dataset.perProdotto = primoProdotto;
          return;
        }
        nuovo.classList.add('cart-drawer__suggerimenti--entra');
        attuale.replaceWith(nuovo);
        if (focusDentro) nuovo.querySelector('button, a')?.focus();
      })
      .catch((e) => {
        if (e.name !== 'AbortError') console.error(e);
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    // product-form chiama gia' cart-drawer.renderContents() con le sezioni
    // ricevute da /cart/add: il secondo fetch di Dawn era doppio e, arrivando
    // dopo, interrompeva l'animazione della barra. Lo saltiamo.
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    // Chi ha gia' ri-renderizzato il cassetto (product-form, "completa il
    // regalo", le modifiche fatte nel cassetto) lo dice con sections['cart-drawer']:
    // saltiamo. Le modifiche fatte nella pagina /cart invece lo ricaricano.
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (this.sezioneGiaAggiornata(event)) return;
      return this.onCartUpdate();
    });
  }

  sezioneGiaAggiornata(event) {
    if (event.source === 'product-form') return true;
    return Boolean(event.cartData && event.cartData.sections && event.cartData.sections['cart-drawer']);
  }

  /*
    Aggiornamento da altre fonti (acquisto rapido multiplo, ecc.): si sostituisce
    tutto .drawer__inner, cosi' anche il passaggio vuoto <-> pieno, la barra, i
    suggerimenti e il conteggio nel titolo restano coerenti.
  */
  onCartUpdate() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const cassetto = document.querySelector('cart-drawer');
        const nuovoCassetto = html.querySelector('cart-drawer');
        const destinazione = cassetto?.querySelector('.drawer__inner');
        const sorgente = html.querySelector('.drawer__inner');
        if (!cassetto || !destinazione || !sorgente) return;
        cassetto.classList.toggle('is-empty', nuovoCassetto ? nuovoCassetto.classList.contains('is-empty') : false);
        destinazione.innerHTML = sorgente.innerHTML;
        if (cassetto.classList.contains('active')) cassetto.caricaCorrelati();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
